import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControlLabel, Stack, Switch } from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerSettingsRoute } from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import MatchSheetPreview from "./MatchSheetPreview";
import { MATCH_SHEET_COLUMN_OPTIONS, matchSheetColumnsFromSettings } from "./match-sheet-settings";

const GestionMatchSheetSettings = () => {
  const intl = useIntl();
  const { customerId } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [columns, setColumns] = useState(matchSheetColumnsFromSettings(null));

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    performRequest("GET", leagueCustomerSettingsRoute(customerId), {}, true, user)
      .then((response) => setColumns(matchSheetColumnsFromSettings(response.data ?? {})))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [customerId, handleError, user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = () => {
    if (!customerId) return;
    setSaving(true);
    performRequest("PATCH", leagueCustomerSettingsRoute(customerId), { match_sheet: { columns } }, true, user)
      .then((response) => {
        setColumns(matchSheetColumnsFromSettings(response.data ?? {}));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.settings.matchSheet.saved" }), { variant: "success" });
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 1180 }}>
      <RevaText variant="h6">{intl.formatMessage({ id: "gestion.settings.matchSheet.title" })}</RevaText>
      <RevaText color="text.secondary">{intl.formatMessage({ id: "gestion.settings.matchSheet.subtitle" })}</RevaText>
      <Alert severity="info">{intl.formatMessage({ id: "gestion.settings.matchSheet.hint" })}</Alert>
      <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={0.5} data-testid="match-sheet-switches">
        {MATCH_SHEET_COLUMN_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.key}
            control={
              <Switch
                checked={Boolean(columns[option.key])}
                disabled={option.locked || saving}
                onChange={(event) =>
                  setColumns((previous) => ({
                    ...previous,
                    [option.key]: event.target.checked,
                    player: true,
                  }))
                }
              />
            }
            label={intl.formatMessage({ id: `gestion.settings.matchSheet.column.${option.key}` })}
          />
        ))}
      </Stack>
      <MatchSheetPreview customerId={customerId} columns={columns} />
      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {intl.formatMessage({ id: "gestion.settings.matchSheet.save" })}
        </Button>
      </Box>
    </Stack>
  );
};

export default GestionMatchSheetSettings;
